import { Container, NetworkIcon } from 'gu-ui-kit'
import { Link } from 'react-router-dom'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import React, { useMemo } from 'react'
import { ROUTES } from 'constants/routes'
import { useTranslation } from 'react-i18next'
import styles from 'modules/Statistics/subcomponents/Layout/Table/statistics-table.module.scss'
import NETWORK_PREFIX_MAP from 'modules/Statistics/networkPrefixMap'
import { createPath } from 'history'
import qs from 'qs'
import prepareFilterParams from 'utils/prepareFilterParams'
import pick from 'lodash/pick'
import { formatByLocale } from 'utils/formatMoney'
import { useSelector } from 'react-redux'
import { StatusBadge } from 'modules/Statistics/subcomponents/StatusBadge'
import networkIconNamesMap from 'modules/Statistics/networkIconNamesMap'

export const useTableConfig = ({
  groupBy,
  filters,
  summary,
  sorting,
  setSorting,
  columnVisibility,
  setColumnVisibility,
  className = '',
}) => {
  const {
    i18n,
    user: { id: userId },
  } = useSelector(({ app }) => app)

  const { t } = useTranslation()
  const pathGen = useGeneratePathI18n()
  const columns = useMemo(() => {
    switch (groupBy) {
      case 'accounts':
        return [
          {
            id: 'account.login',
            header: t('statistics::table.columns.header.account'),
            accessorKey: 'account.login',
            enableHiding: false,
            minWidth: 255,
            className: 'login-cell sticky',
            cell: ({
              // eslint-disable-next-line react/prop-types
              row: {
                // eslint-disable-next-line react/prop-types
                original: { account = {} },
              },
              // eslint-disable-next-line react/prop-types
            }) => (
              <Container
                display="flex"
                className={styles['login-cell']}
                data-content={account.login}
              >
                <div className={'overflow'}>
                  {/* eslint-disable-next-line react/prop-types */}
                  {account.network !== 'vkads' &&
                  // eslint-disable-next-line react/prop-types
                  NETWORK_PREFIX_MAP[account.network] ? (
                    <Link
                      className={styles['navlink']}
                      to={createPath({
                        pathname: pathGen(ROUTES.STATISTICS_ACCOUNT, {
                          // eslint-disable-next-line react/prop-types
                          network: account.network,
                          // eslint-disable-next-line react/prop-types
                          externalId: account.id,
                        }),
                        search: qs.stringify(
                          prepareFilterParams(
                            pick(filters, ['date_group_period', 'date_from', 'date_to']),
                          ),
                          { arrayFormat: 'brackets' },
                        ),
                      })}
                    >
                      {account.login}
                    </Link>
                  ) : (
                    <span className={styles['login']}>{account.login}</span>
                  )}
                </div>
              </Container>
            ),
            footer: ({ table }) => (
              <b className={styles['summary-label']}>
                {t('statistics::table.footer.summary')}:{' '}
                {t('statistics::table.footer.accounts', { count: table.getRowCount() })}
              </b>
            ),
          },
          {
            id: 'account.network',
            header: t('statistics::table.columns.header.network'),
            tooltip: t('statistics::table.columns.header.network_tooltip'),
            accessorKey: 'account.network',
            className: 'network-cell sticky',
            enableHiding: false,
            width: 66,
            cell: ({
              // eslint-disable-next-line react/prop-types
              row: {
                // eslint-disable-next-line react/prop-types
                original: {
                  // eslint-disable-next-line react/prop-types
                  account: { network } = {},
                },
              },
            }) =>
              networkIconNamesMap[network] ? (
                <NetworkIcon
                  name={networkIconNamesMap[network]}
                  size={'m'}
                  width={32}
                  rounded
                />
              ) : null,
          },
          {
            id: 'account.active',
            header: t('statistics::table.columns.header.status'),
            accessorKey: 'account.active',
            width: 80,
            enableHiding: false,
            cell: ({
              row: {
                original: { account: { active } = {} },
              },
            }) => <StatusBadge status={active ? 'new' : 'archived'} />,
          },
          {
            id: 'impressions',
            header: t('statistics::table.columns.header.impressions'),
            accessorKey: 'impressions',
            minWidth: 120,
            width: 120,
            cell: ({ cell }) => (cell.getValue() === null ? '—' : formatByLocale(cell.getValue())),
            footer: () => <b>{formatByLocale(summary.impressions)}</b>,
          },
          {
            //id: filters?.include_vat ? 'cost_with_vat' : 'cost',
            header: t(
              `statistics::table.columns.header.${filters.include_vat ? 'cost_with_vat' : 'cost'}`,
            ),
            accessorKey: filters.include_vat ? 'cost_with_vat' : 'cost',
            minWidth: 150,
            cell: ({ cell }) =>
              cell.getValue() === null
                ? '—'
                : formatByLocale(cell.getValue(), i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {formatByLocale(summary[filters.include_vat ? 'cost_with_vat' : 'cost'], i18n, {
                  maximumFractionDigits: 2,
                })}
              </b>
            ),
          },
          {
            id: 'revenue',
            header: t('statistics::table.columns.header.revenue'),
            accessorKey: 'revenue',
            minWidth: 150,
            cell: ({ cell }) =>
              cell.getValue() === null
                ? '—'
                : formatByLocale(cell.getValue(), i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {formatByLocale(summary.revenue, i18n, {
                  maximumFractionDigits: 2,
                })}
              </b>
            ),
          },
          {
            id: 'clicks',
            header: t('statistics::table.columns.header.clicks'),
            accessorKey: 'clicks',
            minWidth: 100,
            width: 120,
            cell: ({ cell }) => (cell.getValue() === null ? '—' : formatByLocale(cell.getValue())),
            footer: () => <b>{formatByLocale(summary.clicks)}</b>,
          },
          {
            id: 'ctr',
            header: t('statistics::table.columns.header.ctr'),
            tooltip: t('statistics::table.columns.header.ctr_tooltip'),
            accessorKey: 'ctr',
            minWidth: 80,
            width: 100,
            cell: ({ cell }) =>
              cell.getValue() === null
                ? '—'
                : formatByLocale(cell.getValue(), i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => <b>{formatByLocale(summary.ctr, i18n, { maximumFractionDigits: 2 })}</b>,
          },
          {
            id: 'avg_click_price',
            header: t('statistics::table.columns.header.avg_click_price'),
            tooltip: t('statistics::table.columns.header.avg_click_price_tooltip'),
            accessorKey: 'avg_click_price',
            minWidth: 180,
            cell: ({ cell }) =>
              cell.getValue() === null
                ? '—'
                : formatByLocale(cell.getValue(), i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {formatByLocale(summary.avg_click_price, i18n, {
                  maximumFractionDigits: 2,
                })}
              </b>
            ),
          },
          {
            id: 'conversions',
            header: t('statistics::table.columns.header.conversions'),
            accessorKey: 'conversions',
            minWidth: 100,
            cell: ({ cell }) => (cell.getValue() === null ? '—' : formatByLocale(cell.getValue())),
            footer: () => <b>{formatByLocale(summary.conversions)}</b>,
          },
          {
            id: 'conversion_rate',
            header: t('statistics::table.columns.header.conversion_rate'),
            accessorKey: 'conversion_rate',
            minWidth: 150,
            cell: ({ cell }) =>
              cell.getValue() === null
                ? '—'
                : formatByLocale(cell.getValue(), i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {formatByLocale(summary.conversion_rate, i18n, {
                  maximumFractionDigits: 2,
                })}
              </b>
            ),
          },
        ]

      case 'networks':
        return [
          {
            id: 'network',
            header: t('statistics::table.columns.header.network'),
            tooltip: t('statistics::table.columns.header.network_tooltip'),
            accessorKey: 'network',
            className: 'network-cell sticky',
            width: 75,
            enableHiding: false,
            cell: ({ cell }) =>
              networkIconNamesMap[cell.getValue()] ? (
                <NetworkIcon
                  name={networkIconNamesMap[cell.getValue()]}
                  size={'m'}
                  width={32}
                  rounded
                />
              ) : null,
            footer: ({ table }) => (
              <b className={styles['summary-label']}>
                {t('statistics::table.footer.summary')}:{' '}
                {t('statistics::table.footer.networks', { count: table.getRowCount() })}
              </b>
            ),
          },
          {
            id: 'impressions',
            header: t('statistics::table.columns.header.impressions'),
            accessorKey: 'impressions',
            minWidth: 120,
            width: 160,
            cell: ({ cell }) => (cell.getValue() === null ? '—' : formatByLocale(cell.getValue())),
            footer: () => <b>{formatByLocale(summary.impressions)}</b>,
          },
          {
            //id: filters?.include_vat ? 'cost_with_vat' : 'cost',
            header: t(
              'statistics::table.columns.header.' +
                (filters.include_vat ? 'cost_with_vat' : 'cost'),
            ),
            accessorKey: filters?.include_vat ? 'cost_with_vat' : 'cost',
            minWidth: 180,
            cell: ({ cell }) =>
              cell.getValue() === null
                ? '—'
                : formatByLocale(cell.getValue(), i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {formatByLocale(summary[filters.include_vat ? 'cost_with_vat' : 'cost'], i18n, {
                  maximumFractionDigits: 2,
                })}
              </b>
            ),
          },
          {
            id: 'revenue',
            header: t('statistics::table.columns.header.revenue'),
            accessorKey: 'revenue',
            minWidth: 160,
            cell: ({ cell }) =>
              cell.getValue() === null
                ? '—'
                : formatByLocale(cell.getValue(), i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {formatByLocale(summary.revenue, i18n, {
                  maximumFractionDigits: 2,
                })}
              </b>
            ),
          },
          {
            id: 'clicks',
            header: t('statistics::table.columns.header.clicks'),
            accessorKey: 'clicks',
            minWidth: 100,
            cell: ({ cell }) => (cell.getValue() === null ? '—' : formatByLocale(cell.getValue())),
            footer: () => <b>{formatByLocale(summary.clicks)}</b>,
          },
          {
            id: 'ctr',
            header: t('statistics::table.columns.header.ctr'),
            tooltip: t('statistics::table.columns.header.ctr_tooltip'),
            accessorKey: 'ctr',
            minWidth: 80,
            cell: ({ cell }) =>
              cell.getValue() === null
                ? '—'
                : formatByLocale(cell.getValue(), i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => <b>{formatByLocale(summary.ctr, i18n, { maximumFractionDigits: 2 })}</b>,
          },
          {
            id: 'avg_click_price',
            header: t('statistics::table.columns.header.avg_click_price'),
            tooltip: t('statistics::table.columns.header.avg_click_price_tooltip'),
            accessorKey: 'avg_click_price',
            minWidth: 190,
            cell: ({ cell }) =>
              cell.getValue() === null
                ? '—'
                : formatByLocale(cell.getValue(), i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {formatByLocale(summary.avg_click_price, i18n, {
                  maximumFractionDigits: 2,
                })}
              </b>
            ),
          },
          {
            id: 'conversions',
            header: t('statistics::table.columns.header.conversions'),
            accessorKey: 'conversions',
            minWidth: 100,
            cell: ({ cell }) => (cell.getValue() === null ? '—' : formatByLocale(cell.getValue())),
            footer: () => <b>{formatByLocale(summary.conversions)}</b>,
          },
          {
            id: 'conversion_rate',
            header: t('statistics::table.columns.header.conversion_rate'),
            accessorKey: 'conversion_rate',
            minWidth: 200,
            cell: ({ cell }) =>
              cell.getValue() === null
                ? '—'
                : formatByLocale(cell.getValue(), i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {formatByLocale(summary.conversion_rate, i18n, {
                  maximumFractionDigits: 2,
                })}
              </b>
            ),
          },
        ]
    }
  }, [filters?.include_vat, i18n, userId, groupBy, summary])

  return {
    columns,
    state: {
      columnVisibility,
      sorting,
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    showHeader: true,
    showFooter: true,
    enableRowSelection: false,
    className,
  }
}
